import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TWorkitemTemplateAttachment } from "@/types/project-template";

/**
 * Attachments on template work items, keyed by the template work item id.
 * Mirrors ``IssueAttachmentsStore`` but talks to the template attachment
 * endpoints (workspace-scoped, no project). Only saved work items (a real
 * server id, not a ``draft-…`` client key) can carry attachments — the panel
 * gates uploads until the template is saved.
 */
export class WorkitemTemplateAttachmentsStore {
  attachmentsByWorkitemId = observable.map<string, TWorkitemTemplateAttachment[]>();
  loadingByWorkitemId = observable.map<string, boolean>();
  errorByWorkitemId = observable.map<string, string | null>();
  uploadingByWorkitemId = observable.map<string, boolean>();
  uploadErrorByWorkitemId = observable.map<string, string | null>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getAttachments(workitemId: string): TWorkitemTemplateAttachment[] {
    return this.attachmentsByWorkitemId.get(workitemId) ?? [];
  }

  isLoading(workitemId: string): boolean {
    return this.loadingByWorkitemId.get(workitemId) ?? false;
  }

  isUploading(workitemId: string): boolean {
    return this.uploadingByWorkitemId.get(workitemId) ?? false;
  }

  getError(workitemId: string): string | null {
    return this.errorByWorkitemId.get(workitemId) ?? null;
  }

  getUploadError(workitemId: string): string | null {
    return this.uploadErrorByWorkitemId.get(workitemId) ?? null;
  }

  async fetchAttachments(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
  ): Promise<void> {
    this.loadingByWorkitemId.set(workitemId, true);
    this.errorByWorkitemId.set(workitemId, null);
    try {
      const attachments =
        await this.rootStore.projectTemplateService.listWorkitemTemplateAttachments(
          workspaceSlug,
          templateId,
          workitemId,
        );
      runInAction(() => {
        this.attachmentsByWorkitemId.set(workitemId, attachments);
        this.loadingByWorkitemId.set(workitemId, false);
      });
    } catch (error) {
      console.error("Failed to fetch template work-item attachments", {
        workspaceSlug,
        templateId,
        workitemId,
        error,
      });
      runInAction(() => {
        this.loadingByWorkitemId.set(workitemId, false);
        this.errorByWorkitemId.set(workitemId, "Failed to fetch attachments");
      });
    }
  }

  async uploadAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    file: File,
  ): Promise<void> {
    this.uploadingByWorkitemId.set(workitemId, true);
    this.uploadErrorByWorkitemId.set(workitemId, null);
    try {
      const attachment =
        await this.rootStore.projectTemplateService.uploadWorkitemTemplateAttachment(
          workspaceSlug,
          templateId,
          workitemId,
          file,
        );
      runInAction(() => {
        const existing = this.attachmentsByWorkitemId.get(workitemId) ?? [];
        this.attachmentsByWorkitemId.set(workitemId, [attachment, ...existing]);
        this.uploadingByWorkitemId.set(workitemId, false);
      });
    } catch (error) {
      console.error("Failed to upload template work-item attachment", {
        workspaceSlug,
        templateId,
        workitemId,
        fileName: file.name,
        error,
      });
      runInAction(() => {
        this.uploadingByWorkitemId.set(workitemId, false);
        this.uploadErrorByWorkitemId.set(workitemId, "Failed to upload attachment");
      });
    }
  }

  async deleteAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    attachmentId: string,
  ): Promise<void> {
    // Optimistic remove; restore + surface the error on failure.
    const previous = this.attachmentsByWorkitemId.get(workitemId) ?? [];
    runInAction(() => {
      this.attachmentsByWorkitemId.set(
        workitemId,
        previous.filter((a) => a.id !== attachmentId),
      );
    });
    try {
      await this.rootStore.projectTemplateService.deleteWorkitemTemplateAttachment(
        workspaceSlug,
        templateId,
        workitemId,
        attachmentId,
      );
    } catch (error) {
      console.error("Failed to delete template work-item attachment", {
        workspaceSlug,
        templateId,
        workitemId,
        attachmentId,
        error,
      });
      runInAction(() => {
        this.attachmentsByWorkitemId.set(workitemId, previous);
        this.errorByWorkitemId.set(workitemId, "Failed to delete attachment");
      });
    }
  }
}
